import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { db } from "../firebase";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, orderBy, serverTimestamp, writeBatch } from "firebase/firestore";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FaDownload, FaUpload, FaSearch, FaFileExcel, FaTrashAlt, FaEdit, FaTimes } from "react-icons/fa";

export default function OutletMaster() {
  const [outlets, setOutlets] = useState([]);
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterPartyId, setFilterPartyId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [fetchingOutlets, setFetchingOutlets] = useState(false);

  const [form, setForm] = useState({
    partyId: "",
    outletName: "",
    address: "",
    phone: "",
    gstin: "",
    city: "",
    pincode: "",
    stateCode: "29",
    dailyInvoiceLimit: "",
    annualLimit: "",
    status: "active",
  });

  const fetchParties = async () => {
    setLoading(true);
    const snap = await getDocs(query(collection(db, "partyMaster"), orderBy("name")));
    setParties(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  const fetchOutlets = async () => {
    setFetchingOutlets(true);
    try {
      const q = query(
        collection(db, "outletMaster"), 
        orderBy("outletName")
      );
      const snap = await getDocs(q);
      const arr = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setOutlets(arr);
    } catch (err) {
      console.error(err);
      alert("Error fetching outlets");
    } finally {
      setFetchingOutlets(false);
    }
  };

  useEffect(() => {
    fetchParties();
    fetchOutlets();
  }, []);

  const saveOutlet = async () => {
    if (!form.partyId || !form.outletName) {
      alert("Please fill required fields (Party & Outlet Name)");
      return;
    }

    try {
      setLoading(true);
      const party = parties.find(p => p.partyId === form.partyId);
      
      const payload = {
        ...form,
        partyId: form.partyId,
        clientId: party?.clientId || "",
        dailyInvoiceLimit: Number(form.dailyInvoiceLimit || 0),
        annualLimit: Number(form.annualLimit || 0),
        status: form.status,
      };

      if (editingId) {
        await updateDoc(doc(db, "outletMaster", editingId), payload);
        alert("Outlet Updated Successfully!");
      } else {
        await addDoc(collection(db, "outletMaster"), {
          ...payload,
          dailyUsed: 0,
          annualUsed: 0,
          lastInvoiceDate: "",
        });
        alert("Outlet Added Successfully!");
      }

      setForm({
        partyId: "",
        outletName: "",
        address: "",
        phone: "",
        gstin: "",
        city: "",
        pincode: "",
        stateCode: "29",
        dailyInvoiceLimit: "",
        annualLimit: "",
        status: "active",
      });
      setEditingId(null);
      await fetchOutlets();
    } catch (err) {
      console.error(err);
      alert("Error saving outlet");
    } finally {
      setLoading(false);
    }
  };

  const editOutlet = (o) => {
    setEditingId(o.id);
    setForm({
      partyId: o.partyId,
      outletName: o.outletName,
      address: o.address || "",
      phone: o.phone || "",
      gstin: o.gstin || "",
      city: o.city || "",
      pincode: o.pincode || "",
      stateCode: o.stateCode || "29",
      dailyInvoiceLimit: o.dailyInvoiceLimit,
      annualLimit: o.annualLimit,
      status: o.status,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({
      partyId: "",
      outletName: "",
      address: "",
      phone: "",
      gstin: "",
      city: "",
      pincode: "",
      stateCode: "29",
      dailyInvoiceLimit: "",
      annualLimit: "",
      status: "active",
    });
  };

  const toggleStatus = async (outlet) => {
    const newStatus = outlet.status === "active" ? "disabled" : "active";
    await updateDoc(doc(db, "outletMaster", outlet.id), { status: newStatus });
    fetchOutlets();
  };

  const deleteOutlet = async (id) => {
    const ok = window.confirm("Are you sure you want to delete this outlet?");
    if (!ok) return;
    try {
      await deleteDoc(doc(db, "outletMaster", id));
      alert("Outlet deleted");
      setSelectedIds(prev => prev.filter(x => x !== id));
      fetchOutlets();
    } catch (err) {
      console.error(err);
      alert("Error deleting outlet");
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filteredOutlets.map(o => o.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const deleteSelectedOutlets = async () => {
    if (selectedIds.length === 0) return;
    const ok = window.confirm(`Are you sure you want to delete ${selectedIds.length} selected outlets? This action is irreversible.`);
    if (!ok) return;

    setLoading(true);
    try {
      const chunks = [];
      const tempIds = [...selectedIds];
      while (tempIds.length > 0) {
        chunks.push(tempIds.splice(0, 400));
      }

      for (const chunk of chunks) {
        const batch = writeBatch(db);
        chunk.forEach(id => {
          batch.delete(doc(db, "outletMaster", id));
        });
        await batch.commit();
      }

      alert(`Successfully deleted ${selectedIds.length} outlets.`);
      setSelectedIds([]);
      await fetchOutlets();
    } catch (err) {
      console.error(err);
      alert("Error during bulk deletion: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    const data = filteredOutlets.map(o => {
      const p = parties.find(party => party.partyId === o.partyId);
      return {
        "Party Name": p?.name || o.partyId,
        "Outlet Name": o.outletName,
        "Daily Limit": o.dailyInvoiceLimit,
        "Annual Limit": o.annualLimit,
        "Daily Used": o.dailyUsed || 0,
        "Annual Used": o.annualUsed || 0,
        "Address": o.address || "",
        "Phone": o.phone || "",
        "GSTIN": o.gstin || "",
        "City": o.city || "",
        "Pincode": o.pincode || "",
        "State Code": o.stateCode || "",
        "Status": o.status
      };
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Outlets");
    XLSX.writeFile(wb, `Outlets_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const downloadTemplate = () => {
    const data = parties.map(p => ({
      "Party Name": p.name,
      "Outlet Name": "",
      "Daily Limit": "0",
      "Annual Limit": "0",
      "Address": "",
      "Phone": "",
      "GSTIN": "",
      "City": "",
      "Pincode": "",
      "State Code": "29"
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    const wbout = XLSX.write(wb, { bookType: 'csv', type: 'array' });
    saveAs(new Blob([wbout], { type: "text/csv;charset=utf-8;" }), "outlet_upload_template.csv");
  };

  const handleBulkUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const data = results.data;
          const partyNameToId = {};
          parties.forEach(p => partyNameToId[p.name.trim().toLowerCase()] = p.partyId);

          let added = 0;
          for (const row of data) {
            const pName = (row["Party Name"] || "").trim().toLowerCase();
            const oName = (row["Outlet Name"] || "").trim();
            const dLimit = Number(row["Daily Limit"] || 0);
            const aLimit = Number(row["Annual Limit"] || 0);

            if (pName && oName && partyNameToId[pName]) {
              await addDoc(collection(db, "outletMaster"), {
                partyId: partyNameToId[pName],
                outletName: oName,
                dailyInvoiceLimit: dLimit,
                annualLimit: aLimit,
                dailyUsed: 0,
                annualUsed: 0,
                lastInvoiceDate: "",
                address: row["Address"] || "",
                phone: row["Phone"] || "",
                gstin: row["GSTIN"] || "",
                stateCode: row["State Code"] || "",
                status: "active",
                clientId: "mdsons",
                createdAt: serverTimestamp()
              });
              added++;
            }
          }
          alert(`Successfully added ${added} outlets!`);
          fetchOutlets();
        } catch (err) {
          console.error(err);
          alert("Error uploading CSV: " + err.message);
        } finally {
          setUploading(false);
          e.target.value = "";
        }
      }
    });
  };

  const distinctCities = [...new Set(outlets.map(o => o.city).filter(Boolean))].sort();

  const filteredOutlets = outlets.filter(o => {
    const p = parties.find(party => party.partyId === o.partyId);
    
    // Search Term match
    const search = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || (
      o.outletName.toLowerCase().includes(search) ||
      (p?.name || "").toLowerCase().includes(search) ||
      o.partyId.toLowerCase().includes(search) ||
      (o.address || "").toLowerCase().includes(search) ||
      (o.city || "").toLowerCase().includes(search) ||
      (o.gstin || "").toLowerCase().includes(search)
    );

    // Party filter match
    const matchesParty = !filterPartyId || o.partyId === filterPartyId;

    // Status filter match
    const matchesStatus = !filterStatus || o.status === filterStatus;

    // City filter match
    const matchesCity = !filterCity || o.city === filterCity;

    return matchesSearch && matchesParty && matchesStatus && matchesCity;
  });

  const isAllSelected = filteredOutlets.length > 0 && filteredOutlets.every(o => selectedIds.includes(o.id));

  return (
    <Layout>
      <div style={{ padding: "40px 20px", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
        <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
          
          {/* HEADER & GLOBAL ACTIONS */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 32, flexWrap: "wrap", gap: 20 }}>
            <div style={{ borderLeft: "6px solid #4f46e5", paddingLeft: 20 }}>
              <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, color: "#1e293b", letterSpacing: "-0.025em" }}>
                Outlet Master
              </h1>
              <p style={{ marginTop: 4, fontSize: 16, color: "#64748b", fontWeight: 500 }}>
                Manage distribution outlets and set dynamic billing parameters
              </p>
            </div>
            
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={downloadTemplate} style={secondaryActionStyle}>
                📥 Download Template
              </button>
              <label style={primaryActionStyle}>
                {uploading ? "⚙️ SYNCING..." : "📂 BULK CSV UPLOAD"}
                <input type="file" accept=".csv" hidden onChange={handleBulkUpload} disabled={uploading} />
              </label>
              <button onClick={exportToExcel} style={{ ...primaryActionStyle, background: "#10b981" }}>
                 📊 Export Registry
              </button>
            </div>
          </div>

          {/* BULK ACTIONS BANNER */}
          {selectedIds.length > 0 && (
            <div style={{
              background: "#fee2e2",
              border: "1px solid #fca5a5",
              borderRadius: "12px",
              padding: "16px 24px",
              marginBottom: "24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              boxShadow: "0 4px 6px -1px rgba(239, 68, 68, 0.1)"
            }}>
              <div style={{ fontWeight: 700, color: "#991b1b", fontSize: "15px" }}>
                ⚠️ {selectedIds.length} Outlet(s) Selected for Deletion
              </div>
              <div style={{ display: "flex", gap: "12px" }}>
                <button 
                  onClick={() => setSelectedIds([])}
                  style={{
                    padding: "8px 16px",
                    background: "white",
                    border: "1px solid #fca5a5",
                    color: "#991b1b",
                    borderRadius: "8px",
                    fontWeight: 700,
                    cursor: "pointer",
                    fontSize: "13px"
                  }}
                >
                  Clear Selection
                </button>
                <button 
                  onClick={deleteSelectedOutlets}
                  style={{
                    padding: "8px 18px",
                    background: "#ef4444",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: 700,
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(239, 68, 68, 0.2)",
                    fontSize: "13px"
                  }}
                >
                  Delete Selected
                </button>
              </div>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 400px", gap: 32, alignItems: "start" }}>
             
             {/* LEFT SECTOR: FILTER & LIST */}
             <div>
                {/* FILTER DASHBOARD */}
                <div style={cardStyle}>
                  <h3 style={{ ...cardTitleStyle, marginBottom: "16px" }}>🔍 ADVANCED MULTI-FILTERS</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
                    <div>
                      <label style={labelStyle}>PARENT PARTY</label>
                      <select 
                        value={filterPartyId} 
                        onChange={e => { setFilterPartyId(e.target.value); setSelectedIds([]); }}
                        style={inputStyle}
                      >
                        <option value="">All Parties...</option>
                        {parties.map(p => (
                          <option key={p.id} value={p.partyId}>{p.name} ({p.partyId})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>OUTLET STATUS</label>
                      <select 
                        value={filterStatus} 
                        onChange={e => { setFilterStatus(e.target.value); setSelectedIds([]); }}
                        style={inputStyle}
                      >
                        <option value="">All Statuses...</option>
                        <option value="active">Active Only</option>
                        <option value="disabled">Disabled Only</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>CITY / LOCALITY</label>
                      <select 
                        value={filterCity} 
                        onChange={e => { setFilterCity(e.target.value); setSelectedIds([]); }}
                        style={inputStyle}
                      >
                        <option value="">All Cities...</option>
                        {distinctCities.map(city => (
                          <option key={city} value={city}>{city}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-end" }}>
                      <button 
                        onClick={fetchOutlets}
                        disabled={fetchingOutlets}
                        style={{ ...primaryActionStyle, width: "100%", height: "45px" }}
                      >
                        {fetchingOutlets ? "REFRESHING..." : "🔄 REFRESH REGISTRY"}
                      </button>
                    </div>
                  </div>
                </div>

                {/* SEARCH REGISTRY */}
                <div style={{ marginBottom: 20 }}>
                  <input 
                    type="text"
                    placeholder="Search retrieved outlets by name or address..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    style={{ ...inputStyle, padding: "12px 20px" }}
                  />
                </div>

                {/* OUTLET CATALOG TABLE */}
                <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
                  <div style={{ maxHeight: "65vh", overflow: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: "#f8fafc" }}>
                          <th style={{ ...tableHeaderStyle, width: "40px", textAlign: "center" }}>
                            <input 
                              type="checkbox" 
                              checked={isAllSelected}
                              onChange={handleSelectAll}
                              style={{ width: "16px", height: "16px", cursor: "pointer" }}
                            />
                          </th>
                          <th style={tableHeaderStyle}>OUTLET DETAILS</th>
                          <th style={{ ...tableHeaderStyle, textAlign: "right" }}>LIMITS (₹)</th>
                          <th style={{ ...tableHeaderStyle, textAlign: "center" }}>STATUS</th>
                          <th style={{ ...tableHeaderStyle, textAlign: "right" }}>ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOutlets.map((o, idx) => {
                          const p = parties.find(party => party.partyId === o.partyId);
                          const isSelected = selectedIds.includes(o.id);
                          return (
                            <tr key={o.id} style={{ borderBottom: "1px solid #f1f5f9", background: isSelected ? "#fef2f2" : (idx % 2 === 0 ? "#ffffff" : "#fafafa"), transition: "background 0.2s" }}>
                              <td style={{ padding: "16px 20px", textAlign: "center" }}>
                                <input 
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleSelectRow(o.id)}
                                  style={{ width: "16px", height: "16px", cursor: "pointer" }}
                                />
                              </td>
                              <td style={{ padding: "16px 20px" }}>
                                <div style={{ fontWeight: 800, color: "#1e293b", fontSize: "15px" }}>{o.outletName}</div>
                                <div style={{ fontSize: "12px", color: "#64748b", marginTop: 2 }}>{p?.name || o.partyId}</div>
                                <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: 4 }}>{o.address}</div>
                                <div style={{ fontSize: "11px", color: "#4f46e5", fontWeight: 600 }}>{o.city} - {o.pincode}</div>
                              </td>
                              <td style={{ padding: "16px 20px", textAlign: "right" }}>
                                <div style={{ fontWeight: 700, color: "#1e293b" }}>₹{Number(o.dailyInvoiceLimit).toFixed(2)} /D</div>
                                <div style={{ fontSize: "11px", color: "#94a3b8" }}>₹{Number(o.annualLimit || 0).toFixed(2)} /Y</div>
                              </td>
                              <td style={{ padding: "16px 20px", textAlign: "center" }}>
                                <span style={{ 
                                  padding: "4px 10px", 
                                  borderRadius: 6, 
                                  fontSize: "10px", 
                                  fontWeight: 800, 
                                  background: o.status === "active" ? "#dcfce7" : "#fee2e2", 
                                  color: o.status === "active" ? "#166534" : "#991b1b",
                                  letterSpacing: "0.05em"
                                }}>
                                  {o.status.toUpperCase()}
                                </span>
                              </td>
                              <td style={{ padding: "16px 20px", textAlign: "right" }}>
                                <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                                  <button onClick={() => editOutlet(o)} style={tableActionBtnStyle}>Edit</button>
                                  <button onClick={() => toggleStatus(o)} style={{ ...tableActionBtnStyle, color: "#64748b" }}>Toggle</button>
                                  <button onClick={() => deleteOutlet(o.id)} style={{ ...tableActionBtnStyle, color: "#ef4444" }}>Del</button>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                        {filteredOutlets.length === 0 && (
                          <tr>
                            <td colSpan="5" style={{ padding: 100, textAlign: "center" }}>
                              <div style={{ fontSize: 48, marginBottom: 16 }}>📑</div>
                              <h3 style={{ color: "#1e293b", margin: 0 }}>No Outlets Found</h3>
                              <p style={{ color: "#64748b", marginTop: 8 }}>Try adjusting your search query or filters.</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
             </div>

             {/* RIGHT SECTOR: CREATE/EDIT FORM */}
             <div style={{ ...cardStyle, position: "sticky", top: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                  <h3 style={cardTitleStyle}>{editingId ? "✏️ EDIT OUTLET RECORD" : "🆕 REGISTER OUTLET"}</h3>
                  {editingId && <button onClick={cancelEdit} style={tableActionBtnStyle}>Cancel</button>}
                </div>

                <div style={{ display: "grid", gap: "20px" }}>
                  <div>
                    <label style={labelStyle}>PARENT PARTY (REQUIRED)</label>
                    <select 
                      value={form.partyId} 
                      onChange={e => setForm({...form, partyId: e.target.value})}
                      style={inputStyle}
                    >
                      <option value="">Select Party...</option>
                      {parties.map(p => (
                        <option key={p.id} value={p.partyId}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={labelStyle}>OUTLET NAME</label>
                    <input 
                      value={form.outletName}
                      onChange={e => setForm({...form, outletName: e.target.value})}
                      placeholder="Retail Outlet Name"
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    <div>
                      <label style={labelStyle}>DAILY LIMIT (₹)</label>
                      <input type="number" value={form.dailyInvoiceLimit} onChange={e => setForm({...form, dailyInvoiceLimit: e.target.value})} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>ANNUAL LIMIT (₹)</label>
                      <input type="number" value={form.annualLimit} onChange={e => setForm({...form, annualLimit: e.target.value})} style={inputStyle} />
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>ADDRESS & PHONE</label>
                    <input value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="Full physical address" style={{ ...inputStyle, marginBottom: "10px" }} />
                    <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Mobile Number" style={inputStyle} />
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    <div>
                      <label style={labelStyle}>CITY</label>
                      <input value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="e.g. Bangalore" style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>PINCODE</label>
                      <input value={form.pincode} onChange={e => setForm({...form, pincode: e.target.value})} placeholder="6 Digits" style={inputStyle} />
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 80px", gap: "12px" }}>
                    <div>
                      <label style={labelStyle}>GSTIN / STATUS</label>
                      <input value={form.gstin} onChange={e => setForm({...form, gstin: e.target.value})} placeholder="GSTIN Number" style={{ ...inputStyle, marginBottom: "10px" }} />
                      <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} style={inputStyle}>
                          <option value="active">Active</option>
                          <option value="disabled">Disabled</option>
                      </select>
                    </div>
                    <div>
                        <label style={labelStyle}>STATE</label>
                        <input value={form.stateCode} onChange={e => setForm({...form, stateCode: e.target.value})} placeholder="29" style={inputStyle} />
                    </div>
                  </div>

                  <button 
                    onClick={saveOutlet}
                    disabled={loading}
                    style={{ ...primaryActionStyle, width: "100%", height: "50px", marginTop: "12px" }}
                  >
                    {loading ? "SYNCING..." : (editingId ? "UPDATE OUTLET RECORD" : "REGISTER OUTLET")}
                  </button>
                </div>
             </div>

          </div>
        </div>
      </div>
    </Layout>
  );
}

const cardStyle = {
  background: "#ffffff",
  borderRadius: 16,
  padding: "24px",
  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
  border: "1px solid #e2e8f0",
  marginBottom: 24,
};

const cardTitleStyle = {
  margin: 0,
  fontSize: "14px",
  fontWeight: 800,
  color: "#475569",
  letterSpacing: "0.05em",
  textTransform: "uppercase"
};

const primaryActionStyle = {
  padding: "10px 24px",
  fontSize: "14px",
  fontWeight: 700,
  background: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
  color: "#ffffff",
  border: "none",
  borderRadius: "10px",
  cursor: "pointer",
  boxShadow: "0 4px 6px -1px rgba(79, 70, 229, 0.3)",
};

const secondaryActionStyle = {
  ...primaryActionStyle,
  width: "auto",
  background: "#f1f5f9",
  color: "#475569",
  boxShadow: "none",
};

const inputStyle = {
  width: "100%",
  padding: "10px 14px",
  fontSize: "14px",
  borderRadius: "10px",
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
  color: "#1e293b",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
};

const labelStyle = {
  display: "block",
  fontSize: "10px",
  fontWeight: 800,
  color: "#94a3b8",
  marginBottom: "6px",
  letterSpacing: "0.05em",
};

const tableHeaderStyle = {
  padding: "16px 20px",
  fontSize: "11px",
  fontWeight: 800,
  color: "#64748b",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  textAlign: "left",
  borderBottom: "1px solid #e2e8f0"
};

const tableActionBtnStyle = {
  background: "none",
  border: "none",
  color: "#4f46e5",
  fontWeight: 700,
  fontSize: "13px",
  cursor: "pointer",
  padding: "4px 6px"
};
